import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { StepFlow } from "@/components/StepFlow";
import { Users, Phone, Check, PhoneCall, Stack, Clock, Trash, Plus, Robot, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";

export default function Pbx() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["pbx"], queryFn: async () => (await api.get("/pbx")).data });
  const { data: myDids } = useQuery({ queryKey: ["dids-mine"], queryFn: async () => (await api.get("/dids/mine")).data });
  const pbx = data?.pbx;

  const [step, setStep] = useState(0);
  const [userCount, setUserCount] = useState(5);
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);

  const dids = myDids?.dids || [];

  const setup = async () => {
    const chosen = number || dids[0]?.number || "";
    if (!chosen) { toast.error("Add a phone number to your account first, then activate PBX."); setStep(1); return; }
    setBusy(true);
    try {
      await api.post("/pbx/setup", { user_count: Number(userCount), number: chosen });
      toast.success("Cloud PBX activated");
      qc.invalidateQueries({ queryKey: ["pbx"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (isLoading) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  // ---------------- SETUP (Users -> Number -> Activate) ----------------
  if (!pbx) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Cloud PBX</h1>
          <p className="mt-2 text-muted-foreground">Users → Number → Activate. Extensions are created automatically.</p>
        </div>
        <StepFlow steps={["Users", "Number", "Activate"]} current={step} />

        {step === 0 && (
          <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">How many users?</label>
            <div className="mt-3 flex items-center gap-4">
              <input data-testid="pbx-usercount" type="range" min="1" max="50" value={userCount} onChange={(e) => setUserCount(e.target.value)} className="flex-1 accent-[#1D4ED8]" />
              <span className="font-mono-metric text-2xl font-semibold">{userCount}</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">We'll create extensions 1001–{1000 + Number(userCount)}.</p>
            <button data-testid="pbx-users-next" onClick={() => setStep(1)} className="mt-6 w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground">Continue</button>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
            <button onClick={() => setStep(0)} className="mb-4 rounded-full border border-border px-3 py-1.5 text-sm">← Users</button>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Main number</label>
            {dids.length > 0 ? (
              <>
                <div className="mt-2 space-y-2">
                  {dids.map((d) => (
                    <button key={d.id} data-testid={`pbx-did-${d.number}`} onClick={() => setNumber(d.number)}
                      className={`flex w-full items-center justify-between rounded-xl border p-3 ${number === d.number ? "border-primary bg-accent" : "border-border"}`}>
                      <span className="font-mono-metric">{d.number}</span><span className="text-xs text-muted-foreground">{d.country}</span>
                    </button>
                  ))}
                </div>
                <button data-testid="pbx-number-next" onClick={() => setStep(2)}
                  className="mt-6 w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground">Continue</button>
              </>
            ) : (
              <div className="mt-3 rounded-2xl border border-dashed border-border bg-secondary/40 p-5 text-center" data-testid="pbx-no-number">
                <Phone size={28} weight="duotone" className="mx-auto text-primary" />
                <p className="mt-3 text-sm font-medium">You don't have a phone number yet</p>
                <p className="mt-1 text-xs text-muted-foreground">Cloud PBX routes calls to a real number on your account. Get one first, then come back to activate.</p>
                <Link to="/app/numbers" data-testid="pbx-get-number"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
                  <Plus size={16} weight="bold" /> Get a number
                </Link>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary"><Users size={28} weight="duotone" /></div>
            <h2 className="font-display text-xl font-semibold">Activate Cloud PBX</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Users</dt><dd className="font-medium">{userCount} extensions</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Main number</dt><dd className="font-mono-metric">{number || dids[0]?.number || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Includes</dt><dd className="font-medium">IVR · Ring groups · Hours</dd></div>
            </dl>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep(1)} className="rounded-full border border-border px-5 py-3 text-sm">Back</button>
              <button data-testid="pbx-activate" onClick={setup} disabled={busy}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-60">
                <Check size={18} weight="bold" /> {busy ? "Activating…" : "Activate PBX"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------------- CONFIGURED VIEW ----------------
  return <PbxConfig pbx={pbx} />;
}

function PbxConfig({ pbx }) {
  const qc = useQueryClient();
  const [ivr, setIvr] = useState(pbx.ivr);
  const [hours, setHours] = useState(pbx.business_hours);
  const [busy, setBusy] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.put("/pbx", patch);
      toast.success("PBX updated");
      qc.invalidateQueries({ queryKey: ["pbx"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const updateOption = (i, field, val) => {
    const opts = ivr.options.map((o, idx) => idx === i ? { ...o, [field]: val } : o);
    setIvr({ ...ivr, options: opts });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Cloud PBX</h1>
          <p className="mt-2 text-muted-foreground font-mono-metric">{pbx.main_number} · <span className="text-emerald-600">{pbx.status}</span></p>
        </div>
      </div>

      {/* extensions */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground"><Users size={14} /> Extensions ({pbx.extensions.length})</h2>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {pbx.extensions.map((e) => (
            <div key={e.ext} data-testid={`ext-${e.ext}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono-metric text-lg font-semibold">{e.ext}</span>
                <span className="h-2 w-2 rounded-full bg-emerald-500" title={e.presence} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{e.name}</p>
              <p className="mt-2 text-[10px] uppercase tracking-wide text-primary">{e.voicemail ? "Voicemail on" : "No voicemail"}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* IVR */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><PhoneCall size={16} className="text-primary" weight="duotone" /> IVR / Auto-Attendant</h2>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Greeting</label>
          <textarea data-testid="ivr-greeting" value={ivr.greeting} onChange={(e) => setIvr({ ...ivr, greeting: e.target.value })} rows={2}
            className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          <div className="mt-3 space-y-2">
            {ivr.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary font-mono-metric text-sm font-semibold">{o.digit}</span>
                <select data-testid={`ivr-action-${i}`} value={o.action} onChange={(e) => updateOption(i, "action", e.target.value)}
                  className="rounded-lg border border-input bg-card px-2 py-1.5 text-xs outline-none">
                  <option value="ring_group">Ring group</option>
                  <option value="extension">Extension</option>
                  <option value="voicemail">Voicemail</option>
                </select>
                <input value={o.target} onChange={(e) => updateOption(i, "target", e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-input bg-card px-2 py-1.5 text-xs outline-none focus:border-primary" />
              </div>
            ))}
          </div>
          <button data-testid="save-ivr" onClick={() => save({ ivr })} disabled={busy}
            className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save IVR</button>
        </div>

        {/* Ring groups + hours */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Stack size={16} className="text-primary" weight="duotone" /> Ring Groups</h2>
            {pbx.ring_groups.map((g, i) => (
              <div key={i} className="mb-2 flex items-center justify-between rounded-xl bg-secondary px-3 py-2 text-sm">
                <span className="font-medium">{g.name}</span>
                <span className="text-xs text-muted-foreground">{g.strategy.replace("_", " ")} · {g.members.length} ext</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Clock size={16} className="text-primary" weight="duotone" /> Business Hours</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><label className="text-xs text-muted-foreground">Mon–Fri</label><input data-testid="hours-monfri" value={hours.mon_fri} onChange={(e) => setHours({ ...hours, mon_fri: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-card px-2 py-1.5 outline-none focus:border-primary" /></div>
              <div><label className="text-xs text-muted-foreground">After hours</label><input value={hours.after_hours_action} onChange={(e) => setHours({ ...hours, after_hours_action: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-card px-2 py-1.5 outline-none focus:border-primary" /></div>
            </div>
            <button data-testid="save-hours" onClick={() => save({ business_hours: hours })} disabled={busy}
              className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save hours</button>
          </div>
        </div>
      </div>

      <AIReceptionist pbx={pbx} />
    </div>
  );
}

function AIReceptionist({ pbx }) {
  const qc = useQueryClient();
  const ai = pbx.ai_receptionist || { enabled: true, business_name: "", greeting: "", knowledge: "" };
  const [cfg, setCfg] = useState(ai);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const { data: msgs } = useQuery({ queryKey: ["pbx-messages"], queryFn: async () => (await api.get("/pbx/messages")).data });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/pbx", { ai_receptionist: cfg });
      toast.success("SI receptionist saved");
      qc.invalidateQueries({ queryKey: ["pbx"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const test = async () => {
    if (!msg.trim()) return;
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post("/pbx/receptionist/simulate", { message: msg });
      setResult(data);
      qc.invalidateQueries({ queryKey: ["pbx-messages"] });
    } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  const ACTION_TINT = { route: "bg-primary/15 text-primary", answer: "bg-emerald-100 text-emerald-700",
    take_message: "bg-amber-100 text-amber-700", schedule: "bg-violet-100 text-violet-700" };

  return (
    <div className="mt-6 rounded-2xl border border-primary/30 bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><Robot size={18} className="text-primary" weight="duotone" /> SI Receptionist</h2>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="ai-enabled" type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
          {cfg.enabled ? "Enabled" : "Disabled"}
        </label>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business name</label>
            <input data-testid="ai-business" value={cfg.business_name || ""} onChange={(e) => setCfg({ ...cfg, business_name: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Greeting</label>
            <textarea data-testid="ai-greeting" rows={2} value={cfg.greeting || ""} onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Knowledge (what it can answer)</label>
            <textarea data-testid="ai-knowledge" rows={3} value={cfg.knowledge || ""} onChange={(e) => setCfg({ ...cfg, knowledge: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Languages the receptionist speaks</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {["English", "Spanish", "French", "German", "Arabic", "Hindi", "Portuguese", "Chinese"].map((L) => {
                const on = (cfg.languages || []).includes(L);
                return (
                  <button key={L} type="button" data-testid={`lang-${L}`}
                    onClick={() => setCfg({ ...cfg, languages: on ? cfg.languages.filter((x) => x !== L) : [...(cfg.languages || []), L] })}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${on ? "bg-primary text-primary-foreground" : "border border-border"}`}>{L}</button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Alert email (new message notifications)</label>
            <input data-testid="ai-alert-email" value={cfg.alert_email || ""} onChange={(e) => setCfg({ ...cfg, alert_email: e.target.value })}
              placeholder="staff@company.com"
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <button data-testid="ai-save" onClick={save} disabled={busy}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save receptionist</button>        </div>

        <div className="rounded-2xl bg-secondary p-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Sparkle size={12} weight="fill" /> Test the receptionist</p>
          <div className="flex gap-2">
            <input data-testid="ai-test-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Pretend to be a caller…"
              onKeyDown={(e) => e.key === "Enter" && test()}
              className="min-w-0 flex-1 rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            <button data-testid="ai-test-send" onClick={test} disabled={testing}
              className="flex items-center justify-center rounded-xl bg-primary px-3 text-primary-foreground disabled:opacity-60"><PaperPlaneRight size={16} weight="bold" /></button>
          </div>
          {testing && <p className="mt-3 text-sm text-muted-foreground">Thinking…</p>}
          {result && (
            <div className="mt-3 space-y-2" data-testid="ai-result">
              <div className="flex flex-wrap gap-1.5">
                <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-semibold">intent: {result.intent}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACTION_TINT[result.action] || "bg-card"}`}>{result.action}{result.target ? ` → ${result.target}` : ""}</span>
              </div>
              <div className="rounded-xl bg-card p-3 text-sm">{result.reply}</div>
              {result.message && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">📩 Message taken: {result.message}</div>}
            </div>
          )}
          {msgs?.messages?.length > 0 && (
            <div className="mt-4">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Messages taken ({msgs.messages.length})</p>
              <div className="max-h-32 space-y-1 overflow-y-auto">
                {msgs.messages.map((m, i) => (
                  <div key={i} className="rounded-lg bg-card p-2 text-xs"><span className="font-semibold">{m.intent}:</span> {m.summary}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
